import { VisuallyHidden } from '@overdose/components'
import classNames from 'classnames'
import { Button } from 'components/Button'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import backToTopIcon from '~/public/images/icons/back-to-top-icon.svg'
import { Link } from '../Link'
import styles from './Pagination.module.scss'
import { PaginationProps } from './Pagination.types'

const Pagination = ({
  customWidth,
  showingItemCount,
  totalItemsCount,
  showProgress,
  isLoading = false,
  totalPages,
  action,
  onClick,
  showScrollToTop,
}: PaginationProps) => {
  const percentage = 100
  const router = useRouter()
  const pathName = router?.asPath?.split('?')?.[0]
  const getPageLink = (num) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { slug, ...params } = router.query
    const query = { ...params, p: num }
    if (num === 1) {
      delete query.p
    }
    return {
      pathname: pathName,
      query,
    }
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div
      className={classNames(
        styles.wrapper,
        `text-center w-[196px] ${customWidth} 2xl:w-full`,
        {
          [styles.wideWrapper]: action && showScrollToTop,
        }
      )}>
      {showProgress && !!showingItemCount && !!totalItemsCount ? (
        <>
          <div className='text-primary-heading mb-4'>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegular}>
              Showing {showingItemCount} of {totalItemsCount}
            </Typography>
          </div>
          <div className={classNames(styles.progress, 'mb-8 -mt-0.5')}>
            <div
              className={styles.tumb}
              style={{
                width: `${
                  totalItemsCount < 1
                    ? 0
                    : (percentage * showingItemCount) / totalItemsCount
                }%`,
              }}
            />
          </div>
        </>
      ) : null}
      <div className={styles.paginationButtons}>
        {action && (
          <Button
            className={classNames(action.className, styles.actionButton)}
            status='secondary'
            theme={{ root: 'w-full h-[44px] normal-case !rounded' }}
            ariaLabel={action.title}
            loading={isLoading}
            onClick={onClick}>
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodyRegularBold}>
              {action.title}
            </Typography>
          </Button>
        )}
        {showScrollToTop && (
          <Button
            variant='ghost'
            onClick={scrollToTop}
            theme={{
              root: classNames('!px-0 !no-underline', styles.backToTopButton),
            }}>
            <span className='flex flex-col items-center gap-2'>
              <Image
                src={backToTopIcon}
                className={styles.backToTopIcon}
                alt='^'
              />
              <Typography
                className='!normal-case !no-underline'
                tag={TypographyTag.span}
                variant={TypographyVariant.BodyRegularMedium}>
                Back to Top
              </Typography>
            </span>
          </Button>
        )}
      </div>

      {Array.from({ length: totalPages }, (_, index) => {
        return index + 1
      }).map((num) => {
        return (
          <VisuallyHidden key={num}>
            <Link key={num} href={getPageLink(num)}>{`Page ${num}`}</Link>
          </VisuallyHidden>
        )
      })}
    </div>
  )
}

export default Pagination
